import type { CardTemplate } from '../types';

/**
 * Default `sizes` hint for ~240 CSS px-wide carousel / grid tiles (public + dashboard).
 * Lets the browser pick 240w / 480w / 720w descriptors at 1x / 2x / 3x DPR.
 */
export const RESPONSIVE_TEMPLATE_TILE_SIZES = '(max-width: 576px) 50vw, 240px';

/**
 * Builds an absolute `/api/media/{token}` URL for the configured API host.
 *
 * @param mediaBaseUrl Origin of the API (e.g. `https://media.example.com`); trailing slash tolerated.
 * @param token Unguessable media GUID.
 */
export function buildTemplateMediaTokenUrl(mediaBaseUrl: string, token: string): string {
  const base = mediaBaseUrl.replace(/\/$/, '');
  return `${base}/api/media/${token}`;
}

/**
 * Prefers a mid/large WebP thumbnail token for `<img src>` when variants exist so the
 * browser does not start with a multi‑MB original while `srcset` resolves (PBI #1135).
 * Order: w480, then w720, then w240. Returns `null` when no thumbnail tokens exist.
 *
 * @param template Template with optional {@link CardTemplate.thumbnailTokens}.
 * @param mediaBaseUrl API/media origin.
 */
export function resolvePreferredTemplateStillSrc(
  template: CardTemplate,
  mediaBaseUrl: string,
): string | null {
  const t = template.thumbnailTokens;
  if (!t) {
    return null;
  }

  const token = t.w480 ?? t.w720 ?? t.w240;
  if (!token) {
    return null;
  }

  return buildTemplateMediaTokenUrl(mediaBaseUrl, token);
}

/**
 * Absolute URL for the full animated template asset (MP4).
 *
 * Must not reuse {@link resolveEcardTemplateStillToken}: still tokens are WebP/JPEG
 * and must never be assigned to `<video src>` (PBI #1142).
 *
 * For full still / poster / confirmation resolution see {@link ecardTemplateMediaUrls.ts}.
 *
 * @param template Template whose {@link CardTemplate.mediaToken} points at the video blob.
 * @param mediaBaseUrl API/media origin.
 */
export function resolveTemplateVideoSrc(
  template: CardTemplate,
  mediaBaseUrl: string,
): string | null {
  if (!template.mediaToken) {
    return null;
  }

  return buildTemplateMediaTokenUrl(mediaBaseUrl, template.mediaToken);
}

/**
 * Returns a WebP `srcset` for every populated {@link CardTemplate.thumbnailTokens} width
 * (240w / 480w / 720w descriptors). Omits missing widths so the browser can still pick among
 * the variants that exist. Returns `null` when no thumbnail token URLs are available.
 */
export function buildResponsiveTemplateWebpSrcSet(
  template: CardTemplate,
  mediaBaseUrl: string,
): string | null {
  const t = template.thumbnailTokens;
  if (!t) {
    return null;
  }

  const base = mediaBaseUrl.replace(/\/$/, '');
  const parts: string[] = [];
  if (t.w240) {
    parts.push(`${base}/api/media/${t.w240} 240w`);
  }
  if (t.w480) {
    parts.push(`${base}/api/media/${t.w480} 480w`);
  }
  if (t.w720) {
    parts.push(`${base}/api/media/${t.w720} 720w`);
  }

  if (parts.length === 0) {
    return null;
  }

  return parts.join(', ');
}
